import {
  GetStaticPaths,
  GetStaticPathsContext,
  GetStaticProps,
  GetStaticPropsContext,
} from 'next';
import { RichText } from 'prismic-reactjs';

import React from 'react';
import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from 'utils/apolloClient';
import { getCurrentLocale } from 'utils/getCurrentLocale';

import { IJobSummary } from 'components/sections/JobOpenings/interfaces';
import RecruitmentProcess from 'components/sections/RecruitmentProcess';
import { careerHtmlSerializer } from 'utils/htmlSerializers/careerHtmlSerializer';
import {
  CAREER_BY_NAME_QUERY,
  RECRUITMENT_QUERY,
  SUMMARIZED_JOB_QUERY,
} from 'graphql/queries';
import { prepareSeoFromPageData } from '../../components/core/SEO/interface';
import { Routes } from '../../models/enums/routes';
import BackButton from '../../styledComponents/BackButton/BackButton';
import {
  CareerPageType,
  CareerQueryType,
  CareerRecruitmentType,
} from '../../models/pageTypes/careers/[id]';
import { Page } from '../../models/pageTypes/page';
import { PageQuery } from '../../graphql/model/PageQuery';
import {
  CareerApply,
  CareerContent,
  CareerDescription,
  CareerDetails,
  CareerLocationWorkingHours,
  CareerPosition,
  CareerSalary,
  Inner,
  Wrapper,
} from '../../styles/pages/styled/career/career.styles';

interface IPageProps {
  career: CareerPageType;
  recruitment: CareerRecruitmentType;
}

const Career = ({
  data: {
    page: { career, recruitment },
  },
}: Page<IPageProps>) => {
  return (
    <Wrapper>
      <Inner>
        <BackButton
          color={'white'}
          url={Routes.Careers}
          label={RichText.asText(career.back_button)}
        />

        <CareerContent>
          <CareerPosition>{RichText.asText(career.position)}</CareerPosition>
          <CareerDetails>
            <CareerLocationWorkingHours>
              {RichText.asText(career.location)}
              {career?.working_hours
                ? ' - ' + RichText.asText(career.working_hours)
                : ''}
            </CareerLocationWorkingHours>
            <CareerSalary>{RichText.asText(career.salary)}</CareerSalary>
          </CareerDetails>
          {career.salary_per_hour && career.working_hours_freelance && (
            <CareerDetails
              style={{
                display:
                  RichText.asText(career.salary_per_hour).length > 0
                    ? 'flex'
                    : 'none',
              }}
            >
              <CareerLocationWorkingHours>
                {RichText.asText(career.location)} -{' '}
                {RichText.asText(career.working_hours_freelance)}
              </CareerLocationWorkingHours>
              <CareerSalary>
                {RichText.asText(career.salary_per_hour)}
              </CareerSalary>
            </CareerDetails>
          )}

          <CareerDescription>
            <RichText
              render={career.description}
              htmlSerializer={careerHtmlSerializer}
            />

            <CareerApply
              target="_blank"
              href={career.apply_button_link!.url || ''}
              rel="noreferrer"
            >
              {RichText.asText(career.apply_button_label)}
            </CareerApply>
          </CareerDescription>
        </CareerContent>
      </Inner>

      <RecruitmentProcess
        title={RichText.asText(recruitment.title)}
        items={recruitment.stages}
      />
    </Wrapper>
  );
};

export default Career;

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  if (
    context &&
    context.params &&
    context.params.id &&
    typeof context.params.id === 'string'
  ) {
    const {
      data: { allCareer_pages, allRecruitment_processs, ...defaultData },
    } = await apolloClient.query<PageQuery<CareerQueryType>>(
      getPageQuery(
        {
          pageLang: currentLanguage,
          recruitmentLang: currentLanguage,
          url: context.params.id,
        },
        'career_query',
        [CAREER_BY_NAME_QUERY, RECRUITMENT_QUERY]
      )
    );

    if (allCareer_pages.edges.length === 0) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      };
    }

    const careerIndex = allCareer_pages.edges.findIndex(
      (edge: any) => RichText.asText(edge.node.seo_url) === context.params?.id
    );

    return {
      props: {
        data: {
          ...defaultQueryResult(defaultData),
          seo: prepareSeoFromPageData(allCareer_pages.edges[careerIndex].node),
          page: {
            career: allCareer_pages.edges[careerIndex].node,
            recruitment: allRecruitment_processs.edges[0].node,
          },
        },
      },
    };
  }

  return {
    redirect: {
      destination: '/404',
      permanent: false,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async (
  context: GetStaticPathsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const paths = await getPaths(currentLanguage);

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getCareers = async (currentLanguage: string) => {
  const response = await apolloClient.query(
    getPageQuery(
      { pageLang: currentLanguage },
      'top_articles',
      SUMMARIZED_JOB_QUERY
    )
  );

  return response.data.allCareer_pages.edges;
};

export const getPaths = async (currentLanguage: string) => {
  const careers = await getCareers(currentLanguage);

  const paths = careers
    .map((job: { node: IJobSummary }) => RichText.asText(job.node.seo_url))
    .map((path: string) => ({ params: { id: path } }));

  return paths;
};
